import Termly from 'termly-namespace'

import clearAutoBlocking from './clearAutoBlocking'
import isBlockableNode from './isBlockableNode'
import needsBlocking from './needsBlocking'
import recordLoadedCategories from './recordLoadedCategories'
import setBlockerAttributes from './setBlockerAttributes'

import {
  isIframe,
  isScript,
} from './tagUtils'

const CUSTOM_BLOCK_HELP_URL = 'https://support.termly.io/hc/en-us/articles/32848083153169-How-to-use-Domain-Rules-to-change-Auto-Blocker-s-blocking-behavior'

/**
 * Creates an attribute setter override for an HTMLElement object.
 *
 * @param {string} attr - the name of the attribute to set
 *
 * @returns {Function}
 */
export default function makeAttributeSetter(attr) {
  if ( !attr ) {
    throw new Error('No attribute name supplied to makeAttributeSetter()')
  }

  /**
   * If the attached element has a `src` attribute value that is not
   * to be blocked, or if the element has already been auto-blocked,
   * nothing unusual happens--the supplied `type` value is set on the
   * element.
   *
   * However, if the `src` value is to be blocked, the `type`
   * attribute will be set to the default override value, and other
   * attributes will be added to the element to flag it as having been
   * blocked.
   *
   * @param {string} type - the value to set on the element's `type` attribute
   *
   * @this HTMLElement
   */
  // N.B.: this has to be `function` and not `() => {}` because we're using
  // `this` inside of it.
  return function(value) {
    if ( attr === 'src' ) {
      const url = new URL(value, window.location)
      const isTermly = ( url.origin === Termly.apiBase )

      // We don't want to litter the console with messages about lazily-loaded
      // ResourceBlocker components.
      if ( !isTermly && this.dataset.termlyUnknownParent ) {
        console.debug('[Termly] A script from %o %cmight%c not be categorized correctly ' +
                      'because we could not tell which script added it to the page. ' +
                      '%cIf your application is behaving properly, you may safely ignore this ' +
                      'message%c. Otherwise, you might need to set custom domain rules ' +
                      'in the dashboard. See %s for help.\n\nScript URL: %o',
                      url.host,
                      'font-style: italic',
                      'font-style: normal',
                      'font-weight: bold',
                      'font-weight: normal',
                      CUSTOM_BLOCK_HELP_URL,
                      value)
      }

      // Whatever we decided about the address this element used to carry says
      // nothing about the one arriving now, and the marks recording that
      // decision would otherwise answer for it.
      clearAutoBlocking(this)

      // If we're setting the src attribute, we want to test the __incoming__
      // value, not the one currently assigned to the element. But, of course,
      // if the node isn't blocking or doesn't *need* to be blocked, we just
      // fall back to setting the attribute as normal.
      if ( isBlockableNode(this, value) ) {
        if ( needsBlocking(this, value) ) {
          setBlockerAttributes(this, value)
          return
        }

        // The AutoBlocker had jurisdiction here and let it through, so we note
        // what it answers to, provided it is actually going to fetch anything.
        if ( willLoad(this) ) {
          recordLoadedCategories(this, value)
        }
      }
    }

    this.setAttribute(attr, value)
  }
}

// A script or an iframe fetches nothing until it is in the document, so a
// detached one has loaded nothing to record. Everything else we patch fetches
// the moment its address is set, which is what a tracking pixel relies on.
//
function willLoad(node) {
  if ( isScript(node) || isIframe(node) ) {
    return node.isConnected
  }

  return true
}
